/**
 * CoachPlaybook - Quiz Engine
 * Version: 0.6.0
 * Description: Core quiz logic — generates questions from plays, evaluates answers, tracks progress
 */

/**
 * Generate quiz questions from a play's steps and positions
 * @param {Object} play - The play object with positions, paths, passes, steps
 * @param {Object} options - { types: ['tap','choice','draw'], maxQuestions: number }
 * @returns {Array} Array of question objects
 */
export const generateQuiz = (play, options = {}) => {
  const {
    types = ['tap', 'choice', 'draw'],
    maxQuestions = 20
  } = options;

  const questions = [];
  const positions = play.positions || [];
  const paths = play.paths || [];
  const passes = play.passes || [];
  const steps = play.steps || [];

  // If the play has explicit steps, generate questions from them
  if (steps.length > 0) {
    steps.forEach((step, stepIndex) => {
      const availableTypes = types.filter(t => {
        if (t === 'draw') return paths.length > 0;
        return true;
      });

      if (availableTypes.length === 0) return;

      const type = availableTypes[stepIndex % availableTypes.length];
      const question = buildStepQuestion(play, step, stepIndex, type);
      if (question) questions.push(question);
    });
  }

  // Generate position-based questions from paths and passes
  if (types.includes('tap') && positions.length > 0) {
    // "Which player has the ball at the start?"
    const ballCarrier = positions.find(p => p.hasBall);
    if (ballCarrier) {
      questions.push({
        id: `q-tap-ball-${Date.now()}`,
        stepIndex: 0,
        type: 'tap',
        prompt: 'Which player starts with the ball?',
        correctAnswer: ballCarrier.id,
        options: null,
        hints: [`Look for the ${ballCarrier.positionLabel} position`]
      });
    }

    // "Which player sets the screen?" (if screens exist)
    if (play.screens && play.screens.length > 0) {
      play.screens.forEach((screen, idx) => {
        const screener = positions.find(p => p.id === screen.screenerId);
        if (screener) {
          questions.push({
            id: `q-tap-screen-${idx}-${Date.now()}`,
            stepIndex: idx,
            type: 'tap',
            prompt: `Which player sets the screen in this play?`,
            correctAnswer: screener.id,
            options: null,
            hints: [`The screener is the ${screener.positionLabel}`]
          });
        }
      });
    }
  }

  if (types.includes('choice') && positions.length > 1) {
    // "What does the PG do first?"
    positions.forEach((pos, idx) => {
      if (pos.primaryAction && pos.primaryAction.description) {
        const choiceOptions = generateChoiceOptions(play, idx);
        questions.push({
          id: `q-choice-action-${idx}-${Date.now()}`,
          stepIndex: idx,
          type: 'choice',
          prompt: `What is the ${pos.positionLabel}'s primary action?`,
          correctAnswer: choiceOptions.find(o => o.correct)?.id || choiceOptions[0]?.id,
          options: choiceOptions,
          hints: [`Think about what the ${pos.positionLabel} needs to do in a ${play.type} play`]
        });
      }
    });

    // Pass-based questions
    if (passes.length > 0) {
      passes.forEach((pass, idx) => {
        const fromPos = positions.find(p => p.id === pass.fromId);
        const toPos = positions.find(p => p.id === pass.toId);
        if (fromPos && toPos) {
          const wrongOptions = positions
            .filter(p => p.id !== toPos.id)
            .slice(0, 3)
            .map(p => ({
              id: `opt-${p.id}`,
              label: `${p.positionLabel}${p.playerName ? ` (${p.playerName})` : ''}`,
              correct: false
            }));

          const allOptions = [
            ...wrongOptions,
            {
              id: `opt-${toPos.id}`,
              label: `${toPos.positionLabel}${toPos.playerName ? ` (${toPos.playerName})` : ''}`,
              correct: true
            }
          ];

          questions.push({
            id: `q-choice-pass-${idx}-${Date.now()}`,
            stepIndex: idx,
            type: 'choice',
            prompt: `Who does the ${fromPos.positionLabel} pass to?`,
            correctAnswer: `opt-${toPos.id}`,
            options: shuffleArray(allOptions),
            hints: [`The pass goes from the ${fromPos.positionLabel} to another player`]
          });
        }
      });
    }
  }

  if (types.includes('draw') && paths.length > 0) {
    paths.forEach((path, idx) => {
      const pos = positions.find(p => p.id === path.positionId);
      if (pos && path.points && path.points.length >= 2) {
        questions.push({
          id: `q-draw-path-${idx}-${Date.now()}`,
          stepIndex: idx,
          type: 'draw',
          prompt: `Draw the movement path for the ${pos.positionLabel}`,
          correctAnswer: path.points,
          options: null,
          hints: [
            `The ${pos.positionLabel} starts at their current position`,
            `Follow the ${path.style === 'dashed' ? 'cut' : 'movement'} path`
          ]
        });
      }
    });
  }

  // Deduplicate by prompt, cap at maxQuestions, shuffle
  const seen = new Set();
  const unique = questions.filter(q => {
    if (seen.has(q.prompt)) return false;
    seen.add(q.prompt);
    return true;
  });

  return shuffleArray(unique).slice(0, maxQuestions);
};

/**
 * Build a question from an explicit play step
 */
const buildStepQuestion = (play, step, stepIndex, type) => {
  const positions = play.positions || [];
  const description = step.description || step.text || '';

  if (!description) return null;

  if (type === 'tap') {
    // Find which position is referenced in the step
    const referenced = positions.find(p =>
      description.toLowerCase().includes(p.positionLabel.toLowerCase())
    );
    if (!referenced) return null;

    return {
      id: `q-step-tap-${stepIndex}-${Date.now()}`,
      stepIndex,
      type: 'tap',
      prompt: `Step ${stepIndex + 1}: "${description}" — Tap the player involved.`,
      correctAnswer: referenced.id,
      options: null,
      hints: [`Look for the ${referenced.positionLabel}`]
    };
  }

  if (type === 'choice') {
    const options = [
      { id: `opt-correct-${stepIndex}`, label: description, correct: true },
      { id: `opt-wrong-1-${stepIndex}`, label: 'Set a screen at the elbow', correct: false },
      { id: `opt-wrong-2-${stepIndex}`, label: 'Clear out to the corner', correct: false },
      { id: `opt-wrong-3-${stepIndex}`, label: 'Cut to the basket', correct: false }
    ];

    return {
      id: `q-step-choice-${stepIndex}-${Date.now()}`,
      stepIndex,
      type: 'choice',
      prompt: `What happens in Step ${stepIndex + 1}?`,
      correctAnswer: `opt-correct-${stepIndex}`,
      options: shuffleArray(options),
      hints: [`Review the play's step-by-step instructions`]
    };
  }

  if (type === 'draw') {
    const paths = play.paths || [];
    if (paths.length === 0) return null;

    // Use the path that corresponds to this step if available
    const path = paths[stepIndex % paths.length];
    if (!path || !path.points || path.points.length < 2) return null;

    const pos = positions.find(p => p.id === path.positionId);
    return {
      id: `q-step-draw-${stepIndex}-${Date.now()}`,
      stepIndex,
      type: 'draw',
      prompt: `Step ${stepIndex + 1}: Draw the movement for ${pos ? pos.positionLabel : 'the player'}`,
      correctAnswer: path.points,
      options: null,
      hints: [`Trace the path from start to end position`]
    };
  }

  return null;
};

/**
 * Evaluate a tap-type answer
 * @param {Object} question - The question object
 * @param {string} tappedPositionId - The ID of the position the user tapped
 * @returns {Object} { correct, feedback, correctPositionId }
 */
export const evaluateTapAnswer = (question, tappedPositionId) => {
  const correct = tappedPositionId === question.correctAnswer;
  return {
    correct,
    feedback: correct
      ? 'Correct! Great job identifying the right player.'
      : 'Not quite. Try again or check the hint.',
    correctPositionId: question.correctAnswer
  };
};

/**
 * Evaluate a multiple-choice answer
 * @param {Object} question - The question object
 * @param {string} selectedOptionId - The ID of the selected option
 * @returns {Object} { correct, feedback, correctOptionId }
 */
export const evaluateChoiceAnswer = (question, selectedOptionId) => {
  const correct = selectedOptionId === question.correctAnswer;
  const correctOption = question.options?.find(o => o.correct);
  return {
    correct,
    feedback: correct
      ? 'Correct!'
      : `Incorrect. The right answer was: ${correctOption?.label || 'unknown'}`,
    correctOptionId: question.correctAnswer
  };
};

/**
 * Evaluate a draw-type answer by comparing paths
 * @param {Object} question - The question object
 * @param {Array} drawnPoints - Array of {x, y} from user drawing
 * @param {Array} correctPath - Array of {x, y} for the correct path (defaults to question.correctAnswer)
 * @param {number} tolerance - Percentage of court width for acceptable deviation (default 15)
 * @returns {Object} { correct, score, feedback }
 */
export const evaluateDrawAnswer = (question, drawnPoints, correctPath = null, tolerance = 15) => {
  const target = correctPath || question.correctAnswer;

  if (!drawnPoints || drawnPoints.length < 2 || !target || target.length < 2) {
    return {
      correct: false,
      score: 0,
      feedback: 'Not enough points drawn. Try tracing the full path.'
    };
  }

  const score = calculatePathSimilarity(drawnPoints, target);
  const passingScore = Math.max(40, 100 - (tolerance * 4));

  return {
    correct: score >= passingScore,
    score,
    feedback: score >= 80
      ? 'Excellent path! Nearly perfect match.'
      : score >= passingScore
        ? 'Good enough! The general direction was right.'
        : score >= 20
          ? 'Close, but the path needs more accuracy. Check the hint.'
          : 'The path was too far off. Try following the movement more closely.'
  };
};

/**
 * Calculate similarity between two paths (0-100)
 * Normalizes both to same sample count, then compares point-by-point distance
 * @param {Array} drawnPoints - User's drawn path [{x,y}, ...]
 * @param {Array} correctPoints - Correct path [{x,y}, ...]
 * @returns {number} Similarity score 0-100
 */
export const calculatePathSimilarity = (drawnPoints, correctPoints) => {
  if (!drawnPoints?.length || !correctPoints?.length) return 0;

  const sampleCount = 20;
  const sampledDrawn = resamplePath(drawnPoints, sampleCount);
  const sampledCorrect = resamplePath(correctPoints, sampleCount);

  let totalDistance = 0;
  for (let i = 0; i < sampleCount; i++) {
    const dx = sampledDrawn[i].x - sampledCorrect[i].x;
    const dy = sampledDrawn[i].y - sampledCorrect[i].y;
    totalDistance += Math.sqrt(dx * dx + dy * dy);
  }

  const avgDistance = totalDistance / sampleCount;

  // Max reasonable distance is ~141 (diagonal of 100x100 court)
  // Scale so that avgDistance of 0 = 100, avgDistance of 50+ = 0
  const maxAcceptable = 50;
  const score = Math.max(0, Math.min(100, (1 - avgDistance / maxAcceptable) * 100));

  return Math.round(score);
};

/**
 * Resample a path to a fixed number of evenly-spaced points
 * @param {Array} points - Original path [{x,y}, ...]
 * @param {number} count - Number of output samples
 * @returns {Array} Resampled path
 */
const resamplePath = (points, count) => {
  if (points.length === 0) return Array(count).fill({ x: 0, y: 0 });
  if (points.length === 1) return Array(count).fill(points[0]);

  // Calculate total path length
  let totalLength = 0;
  const segments = [];
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    const len = Math.sqrt(dx * dx + dy * dy);
    segments.push(len);
    totalLength += len;
  }

  if (totalLength === 0) return Array(count).fill(points[0]);

  const result = [];
  const stepLength = totalLength / (count - 1);

  result.push({ x: points[0].x, y: points[0].y });

  let currentDist = 0;
  let segIndex = 0;
  let segProgress = 0;

  for (let i = 1; i < count - 1; i++) {
    const targetDist = stepLength * i;

    while (segIndex < segments.length && currentDist + segments[segIndex] - segProgress < targetDist - currentDist) {
      currentDist += segments[segIndex] - segProgress;
      segProgress = 0;
      segIndex++;
    }

    if (segIndex >= segments.length) {
      result.push({ x: points[points.length - 1].x, y: points[points.length - 1].y });
      continue;
    }

    const remaining = targetDist - currentDist;
    const segLen = segments[segIndex];
    const t = segLen > 0 ? (segProgress + remaining) / segLen : 0;
    const clampedT = Math.min(1, Math.max(0, t));

    result.push({
      x: points[segIndex].x + (points[segIndex + 1].x - points[segIndex].x) * clampedT,
      y: points[segIndex].y + (points[segIndex + 1].y - points[segIndex].y) * clampedT
    });

    currentDist = targetDist;
    segProgress = segProgress + remaining;
  }

  result.push({ x: points[points.length - 1].x, y: points[points.length - 1].y });

  return result;
};

/**
 * Generate multiple-choice options including the correct answer
 * @param {Object} play - The play object
 * @param {number} correctStepIndex - Index of the correct position/step
 * @param {number} count - Number of total options (default 4)
 * @returns {Array} Shuffled array of { id, label, correct }
 */
export const generateChoiceOptions = (play, correctStepIndex, count = 4) => {
  const positions = play.positions || [];
  const correctPos = positions[correctStepIndex];

  if (!correctPos) return [];

  const correctLabel = correctPos.primaryAction?.description || `${correctPos.positionLabel} action`;

  const distractors = [
    'Set a back screen at the elbow',
    'Flash to the high post',
    'Cut baseline to the corner',
    'Pop out to the three-point line',
    'Dive to the rim',
    'Set a ball screen at the top of the key',
    'Relocate to the wing',
    'Pin down for the shooter',
    'Slip the screen early',
    'Space to the weak side'
  ];

  // Remove any distractor that matches the correct answer
  const filteredDistractors = distractors.filter(
    d => d.toLowerCase() !== correctLabel.toLowerCase()
  );

  // Shuffle and pick (count - 1) distractors
  const shuffled = shuffleArray(filteredDistractors).slice(0, count - 1);

  const options = shuffled.map((label, idx) => ({
    id: `opt-wrong-${idx}-${correctStepIndex}`,
    label,
    correct: false
  }));

  options.push({
    id: `opt-correct-${correctStepIndex}`,
    label: correctLabel,
    correct: true
  });

  return shuffleArray(options);
};

/**
 * Calculate quiz progress from an array of answers
 * @param {Array} answers - Array of { questionId, correct }
 * @returns {Object} { total, correct, accuracy, streakCurrent, streakBest }
 */
export const getQuizProgress = (answers) => {
  if (!answers || answers.length === 0) {
    return { total: 0, correct: 0, accuracy: 0, streakCurrent: 0, streakBest: 0 };
  }

  const total = answers.length;
  const correctCount = answers.filter(a => a.correct).length;
  const accuracy = total > 0 ? Math.round((correctCount / total) * 100) : 0;

  let streakCurrent = 0;
  let streakBest = 0;
  let tempStreak = 0;

  for (const answer of answers) {
    if (answer.correct) {
      tempStreak++;
      if (tempStreak > streakBest) streakBest = tempStreak;
    } else {
      tempStreak = 0;
    }
  }

  // Current streak is from the end
  streakCurrent = 0;
  for (let i = answers.length - 1; i >= 0; i--) {
    if (answers[i].correct) {
      streakCurrent++;
    } else {
      break;
    }
  }

  return { total, correct: correctCount, accuracy, streakCurrent, streakBest };
};

/**
 * Shuffle array using Fisher-Yates
 */
const shuffleArray = (arr) => {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};
